import React from 'react'
import parsePhoneNumberFromString, {
  CountryCode,
  getCountryCallingCode,
  AsYouType,
  getExampleNumber,
} from 'libphonenumber-js'
import examples from 'libphonenumber-js/mobile/examples'
import { IMaskInput } from 'react-imask'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import InputAdornment from '@mui/material/InputAdornment'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Box from '@mui/material/Box'
import InputBase from '@mui/material/InputBase'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SearchIcon from '@mui/icons-material/Search'
import { List } from 'react-window'
import { getCountries } from 'libphonenumber-js'

// Custom input component for IMask integration with Material-UI
const MaskedInput = React.forwardRef<HTMLInputElement, any>((props, ref) => {
  const { mask, onChange, onAccept, value, onBlur, ...other } = props
  // Convert '9' to '0' for IMask digit pattern, or use the mask as-is if it's already in IMask format
  const imaskPattern = mask ? mask.replace(/9/g, '0') : ''

  const handleAccept = React.useCallback(
    (maskedValue: string) => {
      // Call onAccept callback if provided
      if (onAccept) {
        onAccept(maskedValue)
      }
      // Also trigger onChange for compatibility
      if (onChange) {
        const syntheticEvent = {
          target: { value: maskedValue },
          currentTarget: { value: maskedValue },
        } as React.ChangeEvent<HTMLInputElement>
        onChange(syntheticEvent)
      }
    },
    [onAccept, onChange]
  )

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Сохраняем значение при потере фокуса
      const currentValue = e.target.value || ''
      if (onAccept && currentValue) {
        onAccept(currentValue)
      }
      // Вызываем оригинальный onBlur если он есть
      if (onBlur) {
        onBlur(e)
      }
    },
    [onAccept, onBlur]
  )

  return (
    <IMaskInput
      {...other}
      mask={imaskPattern}
      inputRef={ref}
      value={value || ''}
      unmask={false}
      lazy={false}
      onAccept={handleAccept}
      onBlur={handleBlur}
    />
  )
})
MaskedInput.displayName = 'MaskedInput'

const getFullTemplate = (country: CountryCode): string => {
  const instance = new AsYouType(country)

  const exampleNumber =
    getExampleNumber(country, examples)?.nationalNumber || ''

  instance.input(exampleNumber)

  const template = instance.getTemplate().replace(/x/g, '9')

  return template
}

interface PhoneInputWithCountryProps {
  value: string
  onChange: (value: string) => void
  name: string
  placeholder?: string
  error?: boolean
  helperText?: string
  required?: boolean
  mask?: string
  defaultCountry?: string
  fullWidth?: boolean
  margin?: 'none' | 'dense' | 'normal'
  sx?: any
  onValidationError?: (error: string) => void
  /** Callback для автосохранения при потере фокуса */
  onBlur?: () => void
}

export function PhoneInputWithCountry({
  value,
  onChange,
  name,
  placeholder,
  error,
  helperText,
  required,
  mask,
  defaultCountry = 'US',
  fullWidth = true,
  margin,
  sx,
  onValidationError,
}: PhoneInputWithCountryProps) {
  const [localValue, setLocalValue] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (value && !localValue) {
      const parsedValue = parsePhoneNumberFromString(value)
      setLocalValue(parsedValue?.nationalNumber || '')
      if (parsedValue?.country) {
        setCountryState(parsedValue.country as CountryCode)
      }
    }
  }, [value])

  const [countryState, setCountryState] = React.useState<CountryCode>(
    defaultCountry as CountryCode
  )

  const { template, nationalNumber } = React.useMemo(() => {
    // Формируем полный номер для парсинга
    const fullNumber = localValue
      ? `+${getCountryCallingCode(countryState)}${localValue}`
      : ''
    const phoneNumber = parsePhoneNumberFromString(fullNumber)

    return {
      nationalNumber: phoneNumber?.nationalNumber,
      internationalNumber: phoneNumber?.number,
      template: getFullTemplate(countryState).replace(/x/g, '9'),
    }
  }, [localValue, countryState])

  React.useEffect(() => {
    // Если поле пустое, ошибки нет
    if (!localValue || localValue.trim() === '') {
      onValidationError?.('')
      return
    }

    // Формируем полный номер для валидации
    const fullNumber = `+${getCountryCallingCode(countryState)}${localValue}`
    const phoneNumber = parsePhoneNumberFromString(fullNumber)

    // Если номер валидный - ошибки нет
    if (phoneNumber?.isValid()) {
      onValidationError?.('')
      return
    }

    // Номер не валиден - определяем причину
    if (!phoneNumber) {
      // Номер не может быть распознан
      onValidationError?.('Phone number is incomplete')
      return
    }

    // Сравниваем длину введенных цифр с ожидаемой длиной из template
    const nationalDigits = phoneNumber.nationalNumber || ''
    const templateDigits = template.replace(/[^09]/g, '').length // Количество позиций для цифр в маске

    if (nationalDigits.length >= templateDigits) {
      // Все символы введены, но номер невалиден
      onValidationError?.('Phone number is invalid')
    } else {
      // Не все символы введены
      onValidationError?.('Phone number is incomplete')
    }
  }, [localValue, countryState, template, onValidationError])

  const [searchQuery, setSearchQuery] = React.useState<string>('')
  const [menuOpen, setMenuOpen] = React.useState<boolean>(false)
  const [imageErrors, setImageErrors] = React.useState<Set<string>>(new Set())
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(0)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const listContainerRef = React.useRef<HTMLDivElement>(null)
  const listRef = React.useRef<{
    readonly element: HTMLDivElement | null
    scrollToRow: (config: {
      index: number
      align?: 'auto' | 'center' | 'end' | 'smart' | 'start'
      behavior?: 'auto' | 'instant' | 'smooth'
    }) => void
  } | null>(null)
  const [listWidth, setListWidth] = React.useState<number>(300)

  // Определяем маску из настроек или используем сохраненный template

  const allCountries = React.useMemo(() => getCountries(), [])

  const regionNames = React.useMemo(
    () => new Intl.DisplayNames(['en'], { type: 'region' }),
    []
  )

  // Фильтруем и сортируем страны по поисковому запросу
  const filteredCountries = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return allCountries
    }

    const query = searchQuery.toLowerCase().trim()

    // Функция для вычисления релевантности
    const getRelevanceScore = (country: CountryCode): number => {
      const countryName = regionNames.of(country)?.toLowerCase() || ''
      const countryCode = getCountryCallingCode(country).toString()
      const countryCodeStr = country.toLowerCase()
      const countryCodeWithPlus = `+${countryCode}`

      // Проверяем, соответствует ли страна запросу
      const matches =
        countryName.includes(query) ||
        countryCode.includes(query) ||
        countryCodeStr.includes(query) ||
        countryCodeWithPlus.includes(query)

      if (!matches) return -1

      // Вычисляем релевантность (чем меньше, тем выше приоритет)
      let score = 1000

      // Наивысший приоритет: название начинается с запроса
      if (countryName.startsWith(query)) {
        score = 0
      }
      // Высокий приоритет: код страны начинается с запроса
      else if (countryCodeStr.startsWith(query)) {
        score = 100
      }
      // Средний приоритет: телефонный код начинается с запроса
      else if (
        countryCode.startsWith(query) ||
        countryCodeWithPlus.startsWith(query)
      ) {
        score = 200
      }
      // Низкий приоритет: название содержит запрос
      else if (countryName.includes(query)) {
        score = 300 + countryName.indexOf(query)
      }
      // Очень низкий приоритет: код содержит запрос
      else {
        score =
          500 +
          (countryCode.indexOf(query) || countryCodeStr.indexOf(query) || 0)
      }

      return score
    }

    // Фильтруем и сортируем по релевантности
    return allCountries
      .map((country) => ({
        country,
        score: getRelevanceScore(country),
      }))
      .filter((item) => item.score >= 0) // Убираем несоответствующие
      .sort((a, b) => a.score - b.score) // Сортируем по релевантности
      .map((item) => item.country) // Возвращаем только страны
  }, [allCountries, searchQuery, regionNames])

  // Обработчик ошибки загрузки изображения
  const handleImageError = React.useCallback((countryCode: string) => {
    setImageErrors((prev) => new Set(prev).add(countryCode))
  }, [])

  // Проверка, есть ли ошибка для изображения
  const hasImageError = React.useCallback(
    (countryCode: string) => imageErrors.has(countryCode),
    [imageErrors]
  )

  const handleCountryChange = (event: any) => {
    const newCountry = event.target.value as CountryCode
    setCountryState(newCountry)
    setMenuOpen(false)
    setSearchQuery('')

    // Обновляем значение в форме с новым кодом страны
    if (localValue) {
      const countryCode = getCountryCallingCode(newCountry)
      const fullNumber = `+${countryCode}${localValue}`
      onChange(fullNumber)
    }

    // Фокусируемся на поле ввода телефона после выбора страны
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  }

  const handleMenuOpen = () => {
    setMenuOpen(true)
    setHighlightedIndex(0) // Сбрасываем выделение на первый элемент
    // Обновляем ширину списка при открытии
    setTimeout(() => {
      searchInputRef.current?.focus()
      // Получаем ширину контейнера меню
      const menuElement = document.querySelector('[role="listbox"]')
        ?.parentElement as HTMLElement
      if (menuElement) {
        const width = menuElement.clientWidth || menuElement.offsetWidth || 300
        setListWidth(width)
      } else if (listContainerRef.current) {
        // Fallback: используем ширину контейнера списка
        setListWidth(listContainerRef.current.clientWidth || 300)
      }
    }, 150)
  }

  const handleMenuClose = (event?: any, reason?: string) => {
    // Не закрываем меню, если клик был на поле поиска или внутри меню
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      // Проверяем, был ли клик на поле поиска
      const target = event?.target as HTMLElement
      if (
        target &&
        (target.closest('[data-search-input]') ||
          target.closest('.MuiInputBase-root') ||
          searchInputRef.current?.contains(target))
      ) {
        return // Не закрываем меню
      }
    }
    setMenuOpen(false)
    setSearchQuery('')
  }

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredCountries.length > 0) {
      // Выбираем выделенный элемент (первый по умолчанию)
      const selectedCountry =
        filteredCountries[highlightedIndex] || filteredCountries[0]
      if (selectedCountry) {
        setCountryState(selectedCountry)
        setMenuOpen(false)
        setSearchQuery('')

        // Обновляем значение в форме с новым кодом страны
        if (localValue) {
          const countryCode = getCountryCallingCode(selectedCountry)
          const fullNumber = `+${countryCode}${localValue}`
          onChange(fullNumber)
        }

        // Фокусируемся на поле ввода телефона после выбора страны
        setTimeout(() => {
          inputRef.current?.focus()
        }, 0)
        e.preventDefault()
        e.stopPropagation()
      }
    } else if (e.key === 'ArrowDown') {
      // Перемещаем выделение вниз
      e.preventDefault()
      if (highlightedIndex < filteredCountries.length - 1) {
        const newIndex = highlightedIndex + 1
        setHighlightedIndex(newIndex)
        // Прокручиваем к выделенному элементу
        if (listRef.current) {
          listRef.current.scrollToRow({ index: newIndex })
        }
      }
    } else if (e.key === 'ArrowUp') {
      // Перемещаем выделение вверх
      e.preventDefault()
      if (highlightedIndex > 0) {
        const newIndex = highlightedIndex - 1
        setHighlightedIndex(newIndex)
        // Прокручиваем к выделенному элементу
        if (listRef.current) {
          listRef.current.scrollToRow({ index: newIndex })
        }
      }
    } else if (e.key === 'Escape') {
      // Закрываем меню при Escape
      setMenuOpen(false)
      setSearchQuery('')
    }
  }

  // Обновляем ширину списка при изменении размера окна
  React.useEffect(() => {
    if (!menuOpen) return

    const updateWidth = () => {
      const menuElement = document.querySelector('[role="listbox"]')
        ?.parentElement as HTMLElement
      if (menuElement) {
        const width = menuElement.clientWidth || menuElement.offsetWidth || 300
        setListWidth(width)
      }
    }

    updateWidth()
    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [menuOpen])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Извлекаем только цифры из введенного значения (национальный номер)
    const inputValue = e.target.value.replace(/[^0-9]/g, '')

    console.log(inputValue)
    setLocalValue(inputValue)

    // Формируем международный номер и передаем в форму
    if (inputValue) {
      const countryCode = getCountryCallingCode(countryState)
      const fullNumber = `+${countryCode}${inputValue}`
      onChange(fullNumber)
    } else {
      onChange('')
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text').replace(/[^0-9+]/g, '')

    setLocalValue(pastedText)

    // Формируем международный номер из нового значения
    if (pastedText) {
      const countryCode = getCountryCallingCode(countryState)
      const fullNumber = `+${countryCode}${pastedText}`
      onChange(fullNumber)
    } else {
      onChange('')
    }
    // // Автоматически определяем страну ТОЛЬКО если номер начинается с "+"
    // if (pastedText.startsWith('+')) {
    //   const parsedNumber =

    //   if (parsedNumber) {
    //     const detectedCountry = parsedNumber.country
    //     if (detectedCountry) {
    //       setCountryState(detectedCountry)
    //     }
    //             // onChange(internationalNumber)

    //     const nationalNum = parsedNumber.nationalNumber || ''
    //     setNationalNumber(nationalNum)

    // }
  }

  // Компонент для отображения флага с обработкой ошибок
  const CountryFlag = React.memo<{ countryCode: string; size?: number }>(
    ({ countryCode, size = 20 }) => {
      const hasError = hasImageError(countryCode)

      if (hasError) {
        return (
          <div
            style={{ width: `${size}px`, height: 'auto', display: 'block' }}
          ></div>
        )
      }

      return (
        <img
          style={{ width: `${size}px`, height: 'auto', display: 'block' }}
          src={`https://flagsapi.com/${countryCode}/flat/64.png`}
          alt={countryCode}
          loading="lazy"
          onError={() => handleImageError(countryCode)}
        />
      )
    }
  )
  CountryFlag.displayName = 'CountryFlag'

  // Компонент для рендеринга элемента в виртуальном списке
  const createCountryRow = (
    countries: CountryCode[],
    regionNames: Intl.DisplayNames,
    handleCountrySelect: (country: CountryCode) => void,
    highlightedIndex: number
  ) => {
    return ({
      index,
      style,
    }: {
      index: number
      style: React.CSSProperties
    }) => {
      const country = countries[index]
      const countryCode = getCountryCallingCode(country)
      const countryName = regionNames.of(country) || country
      const isHighlighted = index === highlightedIndex

      return (
        <div style={style}>
          <MenuItem
            value={country}
            onClick={() => handleCountrySelect(country)}
            selected={isHighlighted}
            sx={{
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              backgroundColor: isHighlighted
                ? 'action.selected'
                : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: '100%' }}
            >
              <CountryFlag countryCode={country} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {countryName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                +{countryCode}
              </Typography>
            </Stack>
          </MenuItem>
        </div>
      )
    }
  }

  const startAdornment = (
    <InputAdornment position="start">
      <Select
        value={countryState}
        onChange={handleCountryChange}
        open={menuOpen}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            py: 0,
            minHeight: 'auto',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
        renderValue={(selected) => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CountryFlag countryCode={selected} />
            <Typography fontSize={14} color="text.primary">
              {`+${getCountryCallingCode(selected as CountryCode)}`}
            </Typography>
          </Stack>
        )}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 400,
              overflow: 'hidden',
            },
            onMouseDown: (e: React.MouseEvent) => {
              // Предотвращаем закрытие меню при клике внутри меню
              e.stopPropagation()
            },
          },
          MenuListProps: {
            sx: {
              padding: 0,
              maxHeight: 400,
            },
            onMouseDown: (e: React.MouseEvent) => {
              // Предотвращаем закрытие меню при клике внутри списка
              e.stopPropagation()
            },
          },
          // Отключаем автоматическое закрытие при клике вне меню только для определенных элементов
          disableAutoFocusItem: true,
        }}
      >
        {/* Поле поиска */}
        <Box
          data-search-input
          sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}
          onMouseDown={(e) => {
            // Предотвращаем закрытие меню при клике на поле поиска
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={(e) => {
            // Предотвращаем закрытие меню при клике на поле поиска
            e.stopPropagation()
          }}
        >
          <InputBase
            inputRef={searchInputRef}
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onMouseDown={(e) => {
              // Предотвращаем закрытие меню при клике на поле поиска
              // НЕ используем preventDefault(), чтобы не блокировать фокус
              e.stopPropagation()
            }}
            onClick={(e) => {
              // Предотвращаем закрытие меню при клике на поле поиска
              e.stopPropagation()
            }}
            onFocus={(e) => {
              // Предотвращаем закрытие меню при фокусе на поле поиска
              e.stopPropagation()
            }}
            onBlur={(e) => {
              // Не закрываем меню при потере фокуса, если фокус переходит на другой элемент внутри меню
              const relatedTarget = e.relatedTarget as HTMLElement
              if (
                relatedTarget &&
                (relatedTarget.closest('[role="listbox"]') ||
                  relatedTarget.closest('[data-search-input]') ||
                  relatedTarget.closest('.MuiMenuItem-root'))
              ) {
                e.stopPropagation()
              }
            }}
            startAdornment={
              <SearchIcon
                sx={{ mx: 1, color: 'text.secondary', fontSize: 20 }}
              />
            }
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                py: 1,
              },
            }}
          />
        </Box>

        {/* Виртуальный список стран */}
        {filteredCountries.length > 0 ? (
          <Box
            ref={listContainerRef}
            sx={{
              height: Math.min(filteredCountries.length * 48, 350),
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <List
              listRef={listRef}
              rowCount={filteredCountries.length}
              rowHeight={48}
              style={{
                height: Math.min(filteredCountries.length * 48, 350),
                width: listWidth || '100%',
              }}
              rowComponent={createCountryRow(
                filteredCountries,
                regionNames,
                (country: CountryCode) => {
                  setCountryState(country)
                  setMenuOpen(false)
                  setSearchQuery('')

                  // Обновляем значение в форме с новым кодом страны
                  if (localValue) {
                    const countryCode = getCountryCallingCode(country)
                    const fullNumber = `+${countryCode}${localValue}`
                    onChange(fullNumber)
                  }

                  // Фокусируемся на поле ввода телефона после выбора страны
                  setTimeout(() => {
                    inputRef.current?.focus()
                  }, 0)
                },
                highlightedIndex
              )}
              rowProps={{} as any}
            />
          </Box>
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Country not found
            </Typography>
          </MenuItem>
        )}
      </Select>
    </InputAdornment>
  )

  const endAdornment = required ? (
    <InputAdornment position="end">
      <ErrorOutlineIcon
        sx={{
          fontSize: '1rem',
          color: 'warning.main',
        }}
      />
    </InputAdornment>
  ) : undefined

  // Объединяем внешнюю ошибку и внутреннюю ошибку валидации
  const hasError = error
  const displayErrorText = error ? helperText : ''

  return (
    <FormControl fullWidth={fullWidth} margin={margin} error={hasError} sx={sx}>
      <TextField
        inputRef={inputRef}
        name={name}
        placeholder={placeholder}
        error={hasError}
        helperText={displayErrorText}
        type="tel"
        fullWidth
        onChange={handleChange}
        onPaste={handlePaste}
        InputProps={{
          startAdornment,
          endAdornment,
          inputComponent: MaskedInput as any,
          inputProps: {
            mask: template || '',
            value: nationalNumber || localValue || '',
          } as any,
        }}
      />
    </FormControl>
  )
}
